import { useEffect, useState, useRef } from "react";
import api, { apiError } from "@/lib/api";
import { BarraElenco, BarraAllegati, Casella, SelezionaTutto, azioniCestino, useSelezione } from "@/components/SelezioneFile";
import { useFormErrors, V, errSummary } from "@/lib/formErrors";
import { SectionTitle } from "@/components/common";
import { Modal, Field, Select } from "@/pages/Notizie";
import { toast } from "sonner";
import { nominaFiles, mostraErroreFile } from "@/components/ConflittoNome";
import { LongTextarea, NoteView } from "@/components/LongText";
import { Plus, Pencil, Trash2, CalendarClock, CheckCircle2, Circle, ListTodo, Search, MessageSquare, Paperclip, Users, X } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { PersonaSearch } from "@/components/PersonaSearch";
import ChiamateReminder from "@/components/ChiamateReminder";
import TaskCrm from "@/components/TaskCrm";

const PRIO = {
  alta: { label: "Alta", cls: "text-red-500 bg-red-400/10 border-red-400/30" },
  media: { label: "Media", cls: "text-[#FC82ED] bg-[#FC82ED]/10 border-[#FC82ED]/30" },
  bassa: { label: "Bassa", cls: "text-slate-500 bg-slate-100 border-slate-300" },
};

const localISO = (d) => {
  const t = new Date(d);
  t.setMinutes(t.getMinutes() - t.getTimezoneOffset());
  return t.toISOString().slice(0, 10);
};
const TODAY = localISO(new Date());
const TOMORROW = localISO(new Date(Date.now() + 86400000));
const DAYAFTER = localISO(new Date(Date.now() + 2 * 86400000));
const fmtDate = (s) => { try { return new Date(s).toLocaleDateString("it-IT", { weekday: "short", day: "2-digit", month: "short", timeZone: "Europe/Rome" }); } catch { return s; } };
export const giorniScaduto = (s) => Math.max(1, Math.round((new Date(`${TODAY}T12:00:00`) - new Date(`${s}T12:00:00`)) / 86400000));
export const scadutoLabel = (s) => { const n = giorniScaduto(s); return `Scaduto da ${n} ${n === 1 ? "giorno" : "giorni"}`; };

const bucketOf = (t) => {
  const s = t.scadenza || TODAY;
  const done = t.stato === "completato";
  if (s < TODAY) return done ? "storico" : "scaduti";
  if (s === TODAY) return "oggi";
  if (s === TOMORROW) return "domani";
  return "futuri";
};

const BASE_TABS = [
  { key: "oggi", label: "Task di oggi" },
  { key: "domani", label: "Task di domani" },
  { key: "futuri", label: "Task futuri" },
  { key: "storico", label: "Storico task" },
];

export default function Task() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("oggi");
  const [modal, setModal] = useState(null); // {mode, t}
  const [rimanda, setRimanda] = useState(null);
  const [sp, setSp] = useSearchParams();
  const commentiId = sp.get("t");
  const [q, setQ] = useState("");
  const [team, setTeam] = useState([]);
  const [cestino, setCestino] = useState([]);
  const { monitoraggio: isDirezione, isDirettore, isSegreteria, isSocio } = useAuth();
  const S = useSelezione();
  const vedeTeam = isDirezione || isDirettore;
  const vedeCestino = vedeTeam || isSegreteria || isSocio;
  const loadTeam = () => {
    if (vedeTeam) api.get("/attivita-team-scaduti").then((r) => setTeam(r.data)).catch(() => {});
    if (vedeCestino) api.get("/attivita-cestino").then((r) => setCestino(r.data)).catch(() => {});
  };
  useEffect(() => { loadTeam(); }, [vedeTeam, vedeCestino]); // eslint-disable-line
  const didInit = useRef(false);

  const load = () => api.get("/attivita").then((r) => {
    setItems(r.data); setLoading(false);
    if (!didInit.current) {
      didInit.current = true;
      const anyScaduti = r.data.some((t) => t.stato !== "completato" && t.scadenza && t.scadenza < TODAY);
      if (anyScaduti) setTab("scaduti");
    }
  }).catch(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const toggle = async (t) => {
    if (t.puo_completare === false) return toast.error("Segnano Fatto l'assegnatario e chi ha creato il task: tu puoi commentare.");
    const nuovo = t.stato === "completato" ? "aperto" : "completato";
    setItems((x) => x.map((i) => (i.id === t.id ? { ...i, stato: nuovo } : i)));
    try { await api.put(`/attivita/${t.id}`, { stato: nuovo }); }
    catch (e) { toast.error(apiError(e)); load(); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Eliminare il task "${t.titolo}"?`)) return;
    try { await api.delete(`/attivita/${t.id}`); setItems((x) => x.filter((i) => i.id !== t.id)); loadTeam(); toast.success("Task eliminato: resta nello Storico come eliminato"); }
    catch (e) { toast.error(apiError(e)); }
  };

  const counts = items.reduce((acc, t) => { const b = bucketOf(t); acc[b] = (acc[b] || 0) + 1; return acc; }, {});
  const scadute = counts.scaduti || 0;
  counts.oggi = (counts.oggi || 0) + scadute;
  counts.team = team.length;
  counts.cestino = cestino.length;
  const TABS = [...(scadute > 0 ? [{ key: "scaduti", label: "Scaduti" }] : []), ...BASE_TABS, ...(vedeTeam ? [{ key: "team", label: "Scaduti del team" }] : []), ...(vedeCestino ? [{ key: "cestino", label: "Cestino task" }] : [])];
  // In "Task di oggi" i task scaduti non completati restano in cima finché non vengono completati o rimandati.
  let list = items.filter((t) => bucketOf(t) === tab || (tab === "oggi" && bucketOf(t) === "scaduti"));
  const qq = q.trim().toLowerCase();
  if (tab === "storico" && qq) list = list.filter((t) => `${t.titolo} ${t.descrizione || ""}`.toLowerCase().includes(qq));
  // sort: scaduti first, open first (except storico), then by date/priority
  const prioRank = { alta: 0, media: 1, bassa: 2 };
  list = list.sort((a, b) => {
    if (tab === "storico") return (b.scadenza || "").localeCompare(a.scadenza || "");
    const as = bucketOf(a) === "scaduti" ? 0 : 1, bs = bucketOf(b) === "scaduti" ? 0 : 1;
    if (as !== bs) return as - bs;
    if (!as && !bs) return (a.scadenza || "").localeCompare(b.scadenza || "");
    const ao = a.stato === "completato" ? 1 : 0, bo = b.stato === "completato" ? 1 : 0;
    if (ao !== bo) return ao - bo;
    return (prioRank[a.priorita] ?? 1) - (prioRank[b.priorita] ?? 1);
  });

  const defaultDate = tab === "domani" ? TOMORROW : tab === "futuri" ? DAYAFTER : TODAY;

  const emptyMsg = {
    scaduti: "Nessun task scaduto. Ottimo lavoro!",
    oggi: "Nessun task per oggi.",
    domani: "Nessun task programmato per domani.",
    futuri: "Nessun task futuro programmato.",
    storico: qq ? "Nessun task trovato nello storico." : "Lo storico è vuoto.",
  };

  return (
    <div className="fade-up" data-testid="task-page">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <SectionTitle sub="I task sono organizzati per giorno. La lista attiva mostra solo il giorno selezionato; nulla viene cancellato, tutto resta nello storico.">Task</SectionTitle>
        <button data-testid="new-task-btn" onClick={() => setModal({ mode: "create" })} className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#FC82ED] hover:bg-[#e06dd2] text-black font-semibold text-sm glow-pink transition-all"><Plus size={16} /> Nuovo task</button>
      </div>

      {!isSegreteria && <ChiamateReminder />}
      {!isSegreteria && <TaskCrm />}

      {!loading && scadute > 0 && (
        <button data-testid="task-scadute-banner" onClick={() => setTab("scaduti")}
                className="w-full mb-5 flex items-center gap-3 p-4 rounded-xl border border-red-300 bg-red-50 text-left hover:-translate-y-0.5 transition-transform">
          <span className="w-9 h-9 rounded-lg bg-red-100 text-red-600 flex items-center justify-center shrink-0"><CalendarClock size={18} /></span>
          <span className="flex-1 text-sm text-red-700"><b>{scadute}</b> {scadute === 1 ? "task scaduto non completato" : "task scaduti non completati"}. Tocca per vederli nella scheda Scaduti.</span>
        </button>
      )}

      <div className="flex gap-1.5 mb-5 flex-wrap" data-testid="task-tabs">
        {TABS.map((t) => {
          const isScaduti = t.key === "scaduti";
          const active = tab === t.key;
          const cls = active
            ? (isScaduti ? "bg-red-500 text-white font-semibold border-transparent" : "bg-[#FC82ED] text-black font-semibold border-transparent glow-pink")
            : (isScaduti ? "bg-red-50 text-red-600 border-red-300 hover:bg-red-100" : "bg-white text-slate-600 border-[var(--border)] hover:bg-slate-50");
          return (
          <button key={t.key} data-testid={`tab-${t.key}`} onClick={() => setTab(t.key)}
                  className={`px-3.5 py-2 rounded-xl text-sm font-medium transition-all border ${cls}`}>
            {t.label} <span className={`ml-1 text-xs ${active ? (isScaduti ? "text-white/70" : "text-black/60") : (isScaduti ? "text-red-400" : "text-[var(--muted)]")}`}>{counts[t.key] || 0}</span>
          </button>
          );
        })}
      </div>

      {tab === "storico" && (
        <div className="relative mb-4 max-w-md">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted)]" />
          <input data-testid="task-storico-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca nello storico (titolo o descrizione)"
                 className="w-full pl-9 pr-3 py-2.5 rounded-xl bg-white border border-[var(--border)] focus:border-[#FC82ED] outline-none text-sm" />
        </div>
      )}

      {tab === "team" ? <TeamScaduti list={team} onChanged={loadTeam} /> : tab === "cestino" ? <CestinoTask list={cestino} onChanged={loadTeam} /> : loading ? (
        <div className="space-y-2">{[...Array(4)].map((_, i) => <div key={i} className="skeleton h-16 rounded-xl" />)}</div>
      ) : list.length === 0 ? (
        <div className="card-immo p-12 text-center">
          <ListTodo className="mx-auto text-[var(--muted)] mb-3" size={32} />
          <p className="text-[var(--muted)] text-sm mb-4">{emptyMsg[tab]}</p>
          {tab !== "storico" && <button onClick={() => setModal({ mode: "create", date: defaultDate })} className="text-[#FC82ED] font-semibold text-sm">+ Nuovo task</button>}
        </div>
      ) : (
        <div className="space-y-2.5" data-testid={`task-list-${tab}`}>
          <BarraElenco sel={S.sel} onAnnulla={S.annulla} onFatto={() => { load(); loadTeam(); }}
            azioni={[...(tab !== "storico" ? [{ k: "fatto", label: "Segna fatto", esito: "segnati come fatti", run: (id) => api.put(`/attivita/${id}`, { stato: "completato" }) }] : []),
              { k: "elimina", label: "Elimina", danger: true, conferma: (n) => `Eliminare ${n} task?`, esito: "eliminati", run: (id) => api.delete(`/attivita/${id}`) }]} />
          {list.length > 0 && <button data-testid="task-seltutto" onClick={() => S.tutti(list.map((x) => x.id))} className="text-xs font-semibold text-slate-600 hover:text-[#111111]">Seleziona tutto ({list.length})</button>}
          {list.map((t) => <TaskRow key={t.id} t={t} scelto={S.sel.has(t.id)} onScegli={() => S.toggle(t.id)} storico={tab === "storico"} onToggle={toggle} onEdit={() => setModal({ mode: "edit", t })} onDelete={() => remove(t)} onRimanda={() => setRimanda(t)} onCommenti={() => setSp({ t: t.id })} />)}
        </div>
      )}

      {tab === "storico" && (isSocio || isSegreteria) && <TaskEliminati list={cestino.filter((t) => !qq || `${t.titolo} ${t.descrizione || ""}`.toLowerCase().includes(qq))} />}

      {commentiId && <CommentiModal id={commentiId} onClose={() => { setSp({}); load(); }} />}

      {rimanda && <RimandaModal t={rimanda} onClose={() => setRimanda(null)} onSaved={() => { setRimanda(null); load(); }} />}

      {modal && <TaskModal mode={modal.mode} t={modal.t} defaultDate={modal.date || defaultDate} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
    </div>
  );
}

function EliminaAltruiModal({ t, onClose, onDone }) {
  const [motivo, setMotivo] = useState("");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!motivo.trim()) return toast.error("Scrivi il motivo");
    setSaving(true);
    try { await api.delete(`/attivita/${t.id}`, { data: { motivo } }); toast.success("Task spostato nel cestino: la persona è stata avvisata"); onDone(); }
    catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <Modal title="Elimina task di un'altra persona" onClose={onClose} onSave={save} saving={saving} saveLabel="Sposta nel cestino" testid="task-elimina-altrui-modal">
      <p className="text-sm text-[var(--muted)]">«{t.titolo}» di <b>{t.owner_name}</b>. Il task va nel cestino dei task (recuperabile) e la persona riceve un avviso con il motivo.</p>
      <div>
        <label className="block text-[11px] font-semibold uppercase tracking-wider text-[var(--muted)] mb-1.5">Motivo *</label>
        <LongTextarea data-testid="task-elimina-motivo" value={motivo} onChange={(e) => setMotivo(e.target.value)} rows={3}
                  className="w-full px-3.5 py-2.5 rounded-lg bg-white border border-[var(--border)] focus:border-[#FC82ED] outline-none text-sm" />
      </div>
    </Modal>
  );
}

function TaskEliminati({ list }) {
  if (!list.length) return null;
  return (
    <div className="mt-6 space-y-2" data-testid="storico-task-eliminati">
      <div className="text-xs font-semibold uppercase tracking-wider text-[var(--muted)]">Task eliminati ({list.length})</div>
      {list.map((t) => (
        <div key={t.id} data-testid={`storico-eliminato-${t.id}`} className="card-immo p-4 opacity-80">
          <div className="flex items-center gap-2"><span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-red-100 text-red-700">eliminato</span>
            <span className="font-semibold text-sm line-through">{t.titolo}</span></div>
          <div className="text-xs mt-1 text-[var(--muted)]"><b className="text-[#111111]">{t.owner_name}</b> · eliminato da {t.cestinato_da} il {fmtDate(t.cestinato_at)} · {t.motivo_eliminazione}</div>
        </div>
      ))}
    </div>
  );
}

function CestinoTask({ list, onChanged }) {
  const { isDirezione, isSegreteria } = useAuth();
  const S = useSelezione();
  const ripristina = async (t) => {
    try { await api.post(`/attivita/${t.id}/ripristina`); toast.success("Task ripristinato"); onChanged(); } catch (e) { toast.error(apiError(e)); }
  };
  if (!list.length) return <div className="card-immo p-12 text-center text-[var(--muted)] text-sm" data-testid="task-cestino-empty">Il cestino dei task è vuoto.</div>;
  return (
    <div className="space-y-2.5" data-testid="task-list-cestino">
      <BarraElenco sel={S.sel} onAnnulla={S.annulla} onFatto={onChanged}
        azioni={azioniCestino((id) => api.post(`/attivita/${id}/ripristina`), isDirezione || isSegreteria ? (id) => api.post(`/attivita/${id}/elimina-definitivo`, { conferma: "ELIMINA" }) : null)} />
      <SelezionaTutto ids={list.map((t) => t.id)} sel={S.sel} onTutti={S.tutti} testid="task-cestino-seltutto" />
      {list.map((t) => (
        <div key={t.id} data-testid={`cestino-task-${t.id}`} className={`card-immo p-4 flex items-start gap-4 ${S.sel.has(t.id) ? "ring-2 ring-[#FC82ED]" : ""}`}>
          <Casella inline on={S.sel.has(t.id)} onToggle={() => S.toggle(t.id)} testid={`cestino-task-sel-${t.id}`} />
          <div className="min-w-0 flex-1">
            <div className="font-semibold text-sm">{t.titolo}</div>
            <div className="text-xs mt-1 text-[var(--muted)]"><b className="text-[#111111]">{t.owner_name}</b> · eliminato da {t.cestinato_da} il {fmtDate(t.cestinato_at)}</div>
            <div className="text-xs mt-1 text-slate-600">Motivo: {t.motivo_eliminazione}</div>
          </div>
          <button data-testid={`cestino-ripristina-${t.id}`} onClick={() => ripristina(t)} className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-[var(--border)] hover:border-[#FC82ED]">Ripristina</button>
        </div>
      ))}
    </div>
  );
}

function TeamScaduti({ list, onChanged }) {
  const [del, setDel] = useState(null);
  if (!list.length) return <div className="card-immo p-12 text-center text-[var(--muted)] text-sm" data-testid="task-team-empty">Nessun task scaduto nel team.</div>;
  return (
    <div className="space-y-2.5" data-testid="task-list-team">
      <p className="text-xs text-[var(--muted)]">Solo chi ha il task può completarlo o rimandarlo. Puoi eliminarlo scrivendo il motivo: finisce nel Cestino task e la persona viene avvisata.</p>
      {list.map((t) => (
        <div key={t.id} data-testid={`team-task-${t.id}`} className="card-immo p-4 flex items-start gap-4">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-sm">{t.titolo}</span>
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase border text-red-500 bg-red-400/10 border-red-400/30">{scadutoLabel(t.scadenza)}</span>
            </div>
            <div className="text-xs mt-1.5 text-[var(--muted)]"><b className="text-[#111111]" data-testid={`team-task-owner-${t.id}`}>{t.owner_name}</b> · era per {fmtDate(t.scadenza)}</div>
          </div>
          <button data-testid={`team-task-elimina-${t.id}`} onClick={() => setDel(t)} className="p-2 rounded-lg hover:bg-red-50 text-red-500" title="Elimina (con motivo)"><Trash2 size={15} /></button>
        </div>
      ))}
      {del && <EliminaAltruiModal t={del} onClose={() => setDel(null)} onDone={() => { setDel(null); onChanged(); }} />}
    </div>
  );
}

function RimandaModal({ t, onClose, onSaved }) {
  const [d, setD] = useState(t.scadenza && t.scadenza >= TODAY ? t.scadenza : TOMORROW);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!d || d < TODAY) return toast.error("Scegli oggi o un giorno futuro");
    setSaving(true);
    try { await api.put(`/attivita/${t.id}`, { scadenza: d }); toast.success("Task rimandato"); onSaved(); }
    catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <Modal title="Rimanda task" onClose={onClose} onSave={save} saving={saving} saveLabel="Rimanda" testid="task-rimanda-modal">
      <p className="text-sm text-[var(--muted)]">«{t.titolo}»</p>
      <Field label="Nuova data" testid="task-rimanda-data" type="date" value={d} onChange={(e) => setD(e.target.value)} />
    </Modal>
  );
}

function TaskRow({ t, storico, onToggle, onEdit, onDelete, onRimanda, onCommenti, scelto, onScegli }) {
  const done = t.stato === "completato";
  const scaduto = !done && t.scadenza && t.scadenza < TODAY;
  const prio = PRIO[t.priorita] || PRIO.media;
  return (
    <div data-testid={`task-item-${t.id}`} className={`relative card-immo p-4 pl-11 flex items-start gap-4 transition-all ${done ? "opacity-70" : ""} ${scelto ? "ring-2 ring-[#FC82ED]" : ""}`}>
      <Casella on={scelto} onToggle={onScegli} testid={`task-sel-${t.id}`} className="top-4 left-3" />
      <button data-testid={`task-checkbox-${t.id}`} onClick={() => onToggle(t)} aria-label="Completa"
              className={`shrink-0 mt-0.5 w-6 h-6 rounded-full flex items-center justify-center transition-all ${done ? "bg-[#FC82ED] text-black" : "border-2 border-[var(--border)] hover:border-[#FC82ED] text-transparent hover:text-[#FC82ED]"}`}>
        {done ? <CheckCircle2 size={15} /> : <Circle size={13} className="opacity-0" />}
      </button>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span data-testid={`task-title-${t.id}`} className={`font-semibold text-sm ${done ? "line-through text-[var(--muted)]" : ""}`}>{t.titolo}</span>
          {done
            ? <span data-testid={`task-status-${t.id}`} className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase border text-emerald-600 bg-emerald-400/10 border-emerald-400/30">Completato</span>
            : scaduto
              ? <span data-testid={`task-status-${t.id}`} className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase border text-red-500 bg-red-400/10 border-red-400/30">{scadutoLabel(t.scadenza)}</span>
              : <span data-testid={`task-status-${t.id}`} className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase border text-slate-500 bg-slate-100 border-slate-300">Aperto</span>}
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase border ${prio.cls}`}>{prio.label}</span>
          {t.sono_condiviso && <span data-testid={`task-condiviso-da-${t.id}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border text-[#B8369F] bg-[#FC82ED]/10 border-[#FC82ED]/40"><Users size={10} /> Condiviso da {t.condiviso_da || "—"}</span>}
          {!t.sono_condiviso && t.condivisi?.length > 0 && <span data-testid={`task-condiviso-con-${t.id}`} className="text-[10px] text-[var(--muted)] inline-flex items-center gap-1"><Users size={10} /> con {t.condivisi.map((c) => c.nome).join(", ")}</span>}
          {t.assegnato_a && t.condiviso_da && !t.sono_condiviso && t.puo_completare && t.assegnato_a !== t.condiviso_da && <span className="text-[10px] text-[var(--muted)]">assegnato a {t.assegnato_a}</span>}
        </div>
        {t.descrizione && <NoteView text={t.descrizione} className={`text-xs mt-1 ${done ? "line-through text-[var(--muted)]" : "text-[var(--muted)]"}`} />}
        {t.riunione_id && <a data-testid={`task-riunione-${t.id}`} href={`/riunioni/${t.riunione_id}`} className="inline-block text-xs font-semibold text-[#B8369F] hover:underline mt-1 mr-3">Apri la riunione</a>}
        <div className={`text-xs mt-1.5 inline-flex items-center gap-1 ${scaduto ? "text-red-500 font-semibold" : "text-[var(--muted)]"}`}>
          <CalendarClock size={12} /> {fmtDate(t.scadenza)}
        </div>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <button data-testid={`commenti-task-${t.id}`} onClick={onCommenti} className="px-2 py-1.5 rounded-lg text-xs font-semibold border border-[var(--border)] hover:border-[#FC82ED] inline-flex items-center gap-1" title="Commenti"><MessageSquare size={13} />{t.n_commenti || ""}</button>
        {!done && t.puo_completare !== false && <button data-testid={`rimanda-task-${t.id}`} onClick={onRimanda} className="px-2.5 py-1.5 rounded-lg text-xs font-semibold border border-[var(--border)] hover:border-[#FC82ED] hover:text-[#B8369F]" title="Rimanda a una nuova data">Rimanda</button>}
        {t.puo_completare !== false && <button data-testid={`edit-task-${t.id}`} onClick={onEdit} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-[#111111]" title="Modifica"><Pencil size={15} /></button>}
        {t.can_delete && <button data-testid={`delete-task-${t.id}`} onClick={onDelete} className="p-2 rounded-lg hover:bg-red-50 text-red-500 hover:text-red-600" title="Elimina"><Trash2 size={15} /></button>}
      </div>
    </div>
  );
}

function TaskModal({ mode, t, defaultDate, onClose, onSaved }) {
  const editing = mode === "edit";
  const [f, setF] = useState({
    titolo: t?.titolo || "",
    descrizione: t?.descrizione || "",
    scadenza: t?.scadenza || defaultDate || TODAY,
    priorita: t?.priorita || "media",
    condivisi_ids: [],
  });
  const [persone, setPersone] = useState([]);
  useEffect(() => { api.get("/azioni/destinatari").then((r) => setPersone(r.data)).catch(() => {}); }, []);
  const giaCondivisi = (t?.condivisi || []).map((c) => c.id);
  const [saving, setSaving] = useState(false);
  const { errors, clear, validate, applyServer, summary } = useFormErrors();
  const set = (k) => (e) => { setF({ ...f, [k]: e.target.value }); clear(k); };

  const save = async () => {
    const errs = validate({ titolo: [V.required], scadenza: [V.required] }, f);
    if (Object.keys(errs).length) return toast.error(errSummary(Object.keys(errs).length));
    setSaving(true);
    try {
      const payload = { titolo: f.titolo.trim(), descrizione: f.descrizione, scadenza: f.scadenza, priorita: f.priorita, condivisi_ids: f.condivisi_ids };
      if (editing) { await api.put(`/attivita/${t.id}`, payload); toast.success("Task aggiornato"); }
      else { await api.post("/attivita", payload); toast.success("Task creato"); }
      onSaved();
    } catch (e) { if (applyServer(e)) return; toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <Modal title={editing ? "Modifica Task" : "Nuovo Task"} onClose={onClose} onSave={save} saving={saving} testid="task-modal" ultimoId={editing ? t?.id : null} summary={summary} scheda value={f} onRestore={setF}>
      <Field label="Titolo" testid="task-titolo" required error={errors.titolo} value={f.titolo} onChange={set("titolo")} />
      <div>
        <label className="block text-[11px] font-semibold uppercase tracking-wider text-[var(--muted)] mb-1.5">Descrizione</label>
        <LongTextarea data-testid="task-descrizione" value={f.descrizione} onChange={set("descrizione")} rows={3}
                  className="w-full px-3.5 py-2.5 rounded-lg bg-white border border-[var(--border)] focus:border-[#FC82ED] focus:ring-2 focus:ring-[#FC82ED]/20 outline-none text-sm transition-colors" />
      </div>
      <div>
        <Field label="Data (obbligatoria)" testid="task-scadenza" type="date" required error={errors.scadenza} value={f.scadenza || ""} onChange={set("scadenza")} />
        <div className="text-[11px] text-[var(--muted)] mt-1">Scegli oggi o un giorno futuro. I task compaiono nella lista attiva il giorno della loro data.</div>
      </div>
      <Select label="Priorità" testid="task-priorita" value={f.priorita} onChange={set("priorita")} opts={[["bassa", "Bassa"], ["media", "Media"], ["alta", "Alta"]]} />
      <div data-testid="task-condividi">
        <label className="block text-[11px] font-semibold uppercase tracking-wider text-[var(--muted)] mb-1.5">Condividi con</label>
        <PersonaSearch testid="task-condividi-search" people={persone} sel={f.condivisi_ids} fissi={giaCondivisi} onChange={(v) => setF({ ...f, condivisi_ids: v })} />
        <div className="text-[11px] text-[var(--muted)] mt-1">Puoi anche scrivere @Nome nella descrizione o nei commenti per aggiungere una persona.</div>
      </div>
    </Modal>
  );
}

function CommentiModal({ id, onClose }) {
  const [d, setD] = useState(null);
  const [testo, setTesto] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const S = useSelezione();
  const load = () => api.get(`/attivita/${id}/commenti`).then((r) => setD(r.data)).catch((e) => { toast.error(apiError(e)); onClose(); });
  useEffect(() => { load(); }, [id]); // eslint-disable-line
  const invia = async () => {
    if (!testo.trim() && !files.length) return;
    setBusy(true);
    const fd = new FormData(); fd.append("testo", testo); files.forEach((f) => fd.append("files", f));
    try { await api.post(`/attivita/${id}/commenti`, fd); setTesto(""); setFiles([]); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const scarica = async (a) => {
    try { const r = await api.get(`/attivita/${id}/allegati/${a.id}`, { responseType: "blob" }); window.open(URL.createObjectURL(r.data), "_blank"); }
    catch (e) { mostraErroreFile(e); }
  };
  const All = ({ a }) => <span className="mt-1 mr-3 inline-flex items-center gap-1.5"><Casella inline on={S.sel.has(a.id)} onToggle={() => S.toggle(a.id)} testid={`task-allegato-sel-${a.id}`} />
    <button data-testid={`task-allegato-${a.id}`} onClick={() => scarica(a)} className="inline-flex items-center gap-1 text-xs underline"><Paperclip size={11} />{a.nome}</button></span>;
  const idsAll = d ? [...(d.allegati || []), ...d.commenti.flatMap((c) => c.allegati || [])].map((a) => a.id) : [];
  return (
    <div className="fixed inset-0 z-[60] bg-black/30 flex items-center justify-center p-4" onClick={onClose}>
      <div data-testid="task-commenti-modal" onClick={(e) => e.stopPropagation()} className="bg-white rounded-3xl p-5 w-full max-w-lg max-h-[88vh] flex flex-col">
        <div className="flex items-start gap-2 mb-2"><div className="flex-1 min-w-0"><h3 className="font-bold text-lg">{d?.titolo || "Task"}</h3>
          {d?.condivisi?.length > 0 && <p className="text-xs text-[var(--muted)]">Condiviso con {d.condivisi.map((c) => c.nome).join(", ")}</p>}</div>
          <button data-testid="task-commenti-chiudi" onClick={onClose} className="p-2 rounded-full hover:bg-slate-100"><X size={16} /></button></div>
        {d?.descrizione && <NoteView text={d.descrizione} className="text-sm text-[var(--muted)] mb-2" />}
        {d?.allegati?.length > 0 && <div className="mb-2">{d.allegati.map((a) => <All key={a.id} a={a} />)}</div>}
        {idsAll.length > 0 && <div className="mb-2"><BarraAllegati S={S} ids={idsAll} tipo="task" id={id} onDone={load} testid="task-allegati" /></div>}
        <div className="text-[11px] font-bold uppercase tracking-wide text-[var(--muted)] mb-1">Commenti</div>
        <div className="flex-1 overflow-y-auto space-y-2 mb-3 min-h-[80px]">
          {!d ? <div className="skeleton h-16 rounded-xl" /> : !d.commenti.length ? <p className="text-sm text-[var(--muted)]">Nessun commento.</p> : d.commenti.map((c) => (
            <div key={c.id} data-testid={`task-commento-${c.id}`} className={`rounded-2xl px-3 py-2 ${c.mio ? "bg-[#FC82ED]/10" : "bg-[#F7F7F8]"}`}>
              <div className="text-[11px] font-bold">{c.autore_nome} <span className="font-normal text-[var(--muted)]">{c.at ? new Date(c.at).toLocaleString("it-IT", { timeZone: "Europe/Rome", dateStyle: "short", timeStyle: "short" }) : ""}</span></div>
              {c.testo && <p className="text-sm whitespace-pre-wrap break-words">{c.testo}</p>}
              {c.allegati.map((a) => <All key={a.id} a={a} />)}
            </div>))}
        </div>
        {files.length > 0 && <div className="flex flex-wrap gap-1.5 pb-2">{files.map((f, i) => <span key={i} className="text-xs px-2 py-1 rounded-full bg-slate-100">{f.name}</span>)}</div>}
        <div className="flex items-end gap-2">
          <input ref={fileRef} type="file" multiple hidden data-testid="task-commento-file" onChange={(e) => { const l = [...e.target.files]; e.target.value = ""; nominaFiles(l).then((n) => setFiles((a) => [...a, ...n])); }} />
          <button data-testid="task-commento-allega" onClick={() => fileRef.current?.click()} className="p-2.5 rounded-full hover:bg-slate-100"><Paperclip size={17} /></button>
          <textarea data-testid="task-commento-testo" value={testo} onChange={(e) => setTesto(e.target.value)} rows={2} placeholder="Scrivi un commento… usa @Nome per coinvolgere qualcuno"
            className="flex-1 resize-none px-3 py-2 rounded-2xl border border-[var(--border)] text-sm" />
          <button data-testid="task-commento-invia" disabled={busy} onClick={invia} className="px-4 py-2.5 rounded-full bg-[#2B2B2B] text-white text-sm font-semibold disabled:opacity-50">Invia</button>
        </div>
      </div>
    </div>
  );
}

